import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, CircleNotch } from "@phosphor-icons/react";
import { faceDescriptor, loadFaceModels, snapshot } from "@/lib/face";

export const FaceCapture = ({ open, title, onClose, onCapture }) => {
  const video = useRef(null);
  const [status, setStatus] = useState("Starting camera…");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { return undefined; }
    let stream; let alive = true;
    setReady(false); setStatus("Starting camera…");
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640, height: 480 }, audio: false });
        if (!alive) { stream.getTracks().forEach((t) => t.stop()); return; }
        video.current.srcObject = stream;
        await video.current.play();
        setStatus("Loading face check…");
        await loadFaceModels();
        if (alive) { setReady(true); setStatus("Look straight at the camera, then press Capture"); }
      } catch (e) {
        if (alive) setStatus(e?.name === "NotAllowedError" ? "Camera permission denied — allow the camera and try again" : `Camera not available: ${e?.message || e}`);
      }
    })();
    return () => { alive = false; if (stream) stream.getTracks().forEach((t) => t.stop()); };
  }, [open]);

  const capture = async () => {
    setBusy(true); setStatus("Checking face…");
    try {
      const descriptor = await faceDescriptor(video.current);
      if (!descriptor) { setStatus("No face found — move closer, face the light and try again"); return; }
      setStatus("Face captured — saving…");
      await onCapture({ descriptor, photo: snapshot(video.current) });
      setStatus("Look straight at the camera, then press Capture");
    } catch (e) {
      setStatus(`Face check failed: ${e?.message || e}. Try again.`);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="face-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="rounded-md overflow-hidden bg-black aspect-[4/3]">
          <video ref={video} playsInline muted className="w-full h-full object-cover -scale-x-100" data-testid="face-video" />
        </div>
        <div className="text-[12.5px] text-muted-foreground flex items-center gap-1.5" data-testid="face-status">
          {(!ready || busy) && <CircleNotch size={13} className="animate-spin" />}{status}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="face-cancel">Cancel</Button>
          <Button onClick={capture} disabled={!ready || busy} data-testid="face-capture"><Camera size={15} className="mr-1.5" />Capture</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
